import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { createCostEntry, getGroups } from '../../../services/supabaseService';

const CostEntryModal = ({ isOpen, onClose, onSuccess, selectedGroup = null }) => {
  const { currentLanguage, t } = useLanguage();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [costData, setCostData] = useState({
    groupId: selectedGroup?.id || '',
    category: 'accommodation',
    description: '',
    amount: '',
    quantity: 1,
    unitCost: '',
    vendor: '',
    referenceNumber: '',
    entryDate: new Date()?.toISOString()?.split('T')?.[0],
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadGroups();
      if (selectedGroup) {
        setCostData(prev => ({ ...prev, groupId: selectedGroup?.id }));
      }
    }
  }, [isOpen, selectedGroup]);

  const loadGroups = async () => {
    try {
      const groupsData = await getGroups();
      setGroups(groupsData || []);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  };

  const categoryOptions = [
    { value: 'accommodation', label: t('financial.accommodation') },
    { value: 'transport', label: t('financial.transport') },
    { value: 'meals', label: t('financial.meals') },
    { value: 'guides', label: t('financial.guides') },
    { value: 'visas', label: t('financial.visasCost') },
    { value: 'other', label: t('financial.other') }
  ];

  const groupOptions = groups?.map(group => ({
    value: group?.id,
    label: `${group?.name} (${group?.type})`
  }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);

    try {
      const costEntry = {
        ...costData,
        amount: parseFloat(costData?.amount),
        quantity: parseInt(costData?.quantity) || 1,
        unitCost: costData?.unitCost ? parseFloat(costData?.unitCost) : null
      };

      await createCostEntry(costEntry);
      
      alert(t('financial.successCostSaved'));
      
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error creating cost entry:', error);
      alert(t('financial.errorCostSaving'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCostData({
      groupId: '',
      category: 'accommodation',
      description: '',
      amount: '',
      quantity: 1,
      unitCost: '',
      vendor: '',
      referenceNumber: '',
      entryDate: new Date()?.toISOString()?.split('T')?.[0],
      notes: ''
    });
    onClose();
  };

  const handleAmountChange = (e) => {
    const amount = e?.target?.value;
    const quantity = costData?.quantity || 1;
    const unitCost = amount && quantity ? (parseFloat(amount) / quantity)?.toFixed(2) : '';
    setCostData({ ...costData, amount, unitCost });
  };

  const handleQuantityChange = (e) => {
    const quantity = e?.target?.value;
    const amount = costData?.amount;
    const unitCost = amount && quantity ? (parseFloat(amount) / parseInt(quantity))?.toFixed(2) : '';
    setCostData({ ...costData, quantity, unitCost });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-warm-lg border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border sticky top-0 bg-card z-10">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground">
            {t('financial.addCostEntry')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={handleClose}
          />
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t('financial.group')}
              options={groupOptions}
              value={costData?.groupId}
              onChange={(value) => setCostData({ ...costData, groupId: value })}
              required
              disabled={!!selectedGroup}
            />

            <Select
              label={t('financial.category')}
              options={categoryOptions}
              value={costData?.category}
              onChange={(value) => setCostData({ ...costData, category: value })}
              required
            />
          </div>

          <Input
            label={t('financial.description')}
            type="text"
            placeholder={t('financial.description')}
            value={costData?.description}
            onChange={(e) => setCostData({ ...costData, description: e?.target?.value })}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label={t('financial.amount')}
              type="number"
              step="0.01"
              placeholder="0.00"
              value={costData?.amount}
              onChange={handleAmountChange}
              required
            />

            <Input
              label={t('financial.quantity')}
              type="number"
              min="1"
              value={costData?.quantity}
              onChange={handleQuantityChange}
            />

            <Input
              label={t('financial.unitCost')}
              type="number"
              step="0.01"
              placeholder="0.00"
              value={costData?.unitCost}
              disabled
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('financial.vendor')}
              type="text"
              placeholder={t('financial.vendor')}
              value={costData?.vendor}
              onChange={(e) => setCostData({ ...costData, vendor: e?.target?.value })}
            />

            <Input
              label={t('financial.referenceNumber')}
              type="text"
              placeholder={t('financial.reference')}
              value={costData?.referenceNumber}
              onChange={(e) => setCostData({ ...costData, referenceNumber: e?.target?.value })}
            />
          </div>

          <Input
            label={t('financial.entryDate')}
            type="date"
            value={costData?.entryDate}
            onChange={(e) => setCostData({ ...costData, entryDate: e?.target?.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t('financial.notes')}
            </label>
            <textarea
              className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
              rows="3"
              placeholder={t('financial.notes')}
              value={costData?.notes}
              onChange={(e) => setCostData({ ...costData, notes: e?.target?.value })}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              {t('financial.cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Check"
              iconPosition="left"
              loading={loading}
            >
              {loading ? t('financial.saving') : t('financial.saveCost')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CostEntryModal;